import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  TruckIcon,
  DocumentTextIcon,
  UserIcon,
  MapPinIcon,
  CreditCardIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ClockIcon,
  ScaleIcon,
  CubeIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { COUNTRIES, DROPOFF_LOCATIONS, CURRENCY, WEIGHT_UNIT, DIMENSION_UNIT, getStateProvinceLabel, getStatesForCountry, hasPredefinedStates } from '../constants/international';

function NewShipment() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Package Information
    trackingNumber: '',
    serviceType: 'standard',
    packageType: 'document',
    weight: '',
    dimensions: { length: '', width: '', height: '' },
    value: '',
    quantity: '1',
    description: '',
    isFragile: false,
    requiresSignature: false,
    insurance: false,
    insuranceValue: '',
    customsValue: '',
    customsDescription: '',
    dangerousGoods: false,
    dangerousGoodsType: '',
    
    // Pickup Information
    pickupType: 'dropoff',
    pickupDate: '',
    pickupTime: '',
    pickupInstructions: '',
    preferredDropoffLocation: '',
    returnService: false,
    returnInstructions: '',
    
    // Sender Information
    senderName: '',
    senderCompany: '',
    senderPhone: '',
    senderEmail: '',
    senderAddress: '',
    senderCity: '',
    senderStateProvince: '',
    senderPostalCode: '',
    senderCountry: '',
    senderTaxId: '',
    
    // Receiver Information
    receiverName: '',
    receiverCompany: '',
    receiverPhone: '',
    receiverEmail: '',
    receiverAddress: '',
    receiverCity: '',
    receiverStateProvince: '',
    receiverPostalCode: '',
    receiverCountry: '',
    receiverTaxId: '',
    
    // Shipping Details
    origin: '',
    destination: '',
    currentLocation: '',
    status: 'Pending',
    priority: 'normal',
    specialInstructions: '',
    timeline: [{ location: '', status: '', timestamp: '', description: '' }],
    dateShipped: '',
    estimatedDelivery: '',
    deliveryConfirmation: false,
    deliveryConfirmationType: 'signature',
    
    // Payment Information
    paymentMethod: 'credit',
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    billingAddress: '',
    billingPostalCode: '',
    billingCountry: '',
    accountNumber: '',
    billingReference: '',
    
    // Additional Services
    additionalServices: {
      saturdayDelivery: false,
      holdForPickup: false,
      temperatureControl: false,
      temperatureRange: '',
      specialHandling: false,
      specialHandlingType: '',
      notificationPreferences: {
        email: true,
        sms: false,
        push: false
      }
    }
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [calculatedPrice, setCalculatedPrice] = useState(null);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const steps = [
    { number: 1, title: 'Package Details', icon: <CubeIcon className="w-6 h-6" /> },
    { number: 2, title: 'Pickup/Dropoff', icon: <TruckIcon className="w-6 h-6" /> },
    { number: 3, title: 'Sender Information', icon: <UserIcon className="w-6 h-6" /> },
    { number: 4, title: 'Receiver Information', icon: <UserIcon className="w-6 h-6" /> },
    { number: 5, title: 'Shipping Options', icon: <ClockIcon className="w-6 h-6" /> },
    { number: 6, title: 'Additional Services', icon: <ShieldCheckIcon className="w-6 h-6" /> },
    { number: 7, title: 'Payment', icon: <CreditCardIcon className="w-6 h-6" /> },
    { number: 8, title: 'Review & Submit', icon: <CheckCircleIcon className="w-6 h-6" /> }
  ];

  const serviceTypes = [
    { 
      value: 'standard', 
      label: 'Next-Day Delivery', 
      price: 199.99, 
      time: 'Next Business Day',
      description: 'Reliable delivery for non-urgent shipments',
      features: ['Tracking included', 'Delivery confirmation', 'Standard insurance']
    },
    { 
      value: 'express', 
      label: 'Same-Day Delivery', 
      price: 299.99, 
      time: 'Same Day',
      description: 'Fast delivery for time-sensitive packages',
      features: ['Priority handling', 'Real-time tracking', 'Enhanced insurance']
    },
    { 
      value: 'rush', 
      label: 'Rush Delivery', 
      price: 499.99, 
      time: '1-3 Hours',
      description: 'Urgent delivery for critical shipments',
      features: ['Dedicated courier', 'Live tracking', 'Maximum insurance']
    },
    { 
      value: 'medical', 
      label: 'Medical Courier', 
      price: 399.99, 
      time: 'Same Day',
      description: 'Specialized handling for medical shipments',
      features: ['Temperature control', 'HIPAA compliant', 'Chain of custody']
    }
  ];

  const packageTypes = [
    { 
      value: 'document', 
      label: 'Documents', 
      icon: <DocumentTextIcon className="w-6 h-6" />,
      maxWeight: 2,
      description: 'Letters, contracts, and important papers'
    },
    { 
      value: 'package', 
      label: 'Package', 
      icon: <CubeIcon className="w-6 h-6" />,
      maxWeight: 70,
      description: 'Regular boxes and parcels'
    },
    { 
      value: 'fragile', 
      label: 'Fragile', 
      icon: <ExclamationTriangleIcon className="w-6 h-6" />,
      maxWeight: 50,
      description: 'Items requiring special handling'
    },
    { 
      value: 'electronics', 
      label: 'Electronics', 
      icon: <InformationCircleIcon className="w-6 h-6" />,
      maxWeight: 40,
      description: 'Electronic devices and equipment'
    }
  ];

  const dropoffLocations = DROPOFF_LOCATIONS;

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prevState => ({
        ...prevState,
        [parent]: {
          ...prevState[parent],
          [child]: type === 'checkbox' ? checked : value
        }
      }));
    } else {
      setFormData(prevState => ({
        ...prevState,
        [name]: type === 'checkbox' ? checked : value
      }));
    }
    
    // Clear state/province when country changes
    if (name === 'senderCountry') {
      setFormData(prevState => ({
        ...prevState,
        senderStateProvince: ''
      }));
    }
    if (name === 'receiverCountry') {
      setFormData(prevState => ({
        ...prevState,
        receiverStateProvince: ''
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Recalculate price when relevant fields change
    if (['serviceType', 'weight', 'packageType', 'insurance'].includes(name)) {
      calculatePrice();
    }
  };

  const calculatePrice = () => {
    const baseService = serviceTypes.find(s => s.value === formData.serviceType);
    if (!baseService) return;

    let total = baseService.price;
    
    // Add weight-based pricing ($ per kg)
    if (formData.weight) {
      total += parseFloat(formData.weight) * 12.99; // $12.99 per kg
    }

    // Add insurance cost
    if (formData.insurance && formData.insuranceValue) {
      total += parseFloat(formData.insuranceValue) * 0.02; // 2% of insured value
    }

    // Add additional services costs
    if (formData.additionalServices.saturdayDelivery) total += 25.99;
    if (formData.additionalServices.holdForPickup) total += 15.99;
    if (formData.additionalServices.temperatureControl) total += 35.99;

    setCalculatedPrice(total.toFixed(2));
  };

  const handleTimelineChange = (index, field, value) => {
    const newTimeline = [...formData.timeline];
    newTimeline[index][field] = value;
    setFormData(prevState => ({
      ...prevState,
      timeline: newTimeline
    }));
  };

  const addTimelineEntry = () => {
    setFormData(prevState => ({
      ...prevState,
      timeline: [...prevState.timeline, { location: '', status: '', timestamp: '', description: '' }]
    }));
  };

  const removeTimelineEntry = (index) => {
    if (formData.timeline.length > 1) {
      const newTimeline = formData.timeline.filter((_, i) => i !== index);
      setFormData(prevState => ({
        ...prevState,
        timeline: newTimeline
      }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};
    
    switch (step) {
      case 1: // Package Details
        if (!formData.serviceType) newErrors.serviceType = 'Service type is required';
        if (!formData.packageType) newErrors.packageType = 'Package type is required';
        if (!formData.weight) newErrors.weight = 'Weight is required';
        if (formData.weight && parseFloat(formData.weight) <= 0) {
          newErrors.weight = 'Weight must be greater than 0';
        }
        if (!formData.description) newErrors.description = 'Package description is required';
        if (formData.insurance && !formData.insuranceValue) {
          newErrors.insuranceValue = 'Insurance value is required when insurance is selected';
        }
        break;

      case 2: // Pickup/Dropoff
        if (!formData.pickupType) newErrors.pickupType = 'Pickup type is required';
        if (formData.pickupType === 'pickup') {
          if (!formData.pickupDate) newErrors.pickupDate = 'Pickup date is required';
          if (!formData.pickupTime) newErrors.pickupTime = 'Pickup time is required';
        }
        if (formData.pickupType === 'dropoff' && !formData.preferredDropoffLocation) {
          newErrors.preferredDropoffLocation = 'Dropoff location is required';
        }
        break;

      case 3: // Sender Information
        if (!formData.senderName) newErrors.senderName = 'Sender name is required';
        if (!formData.senderPhone) newErrors.senderPhone = 'Sender phone is required';
        if (!formData.senderEmail) newErrors.senderEmail = 'Sender email is required';
        if (!formData.senderAddress) newErrors.senderAddress = 'Sender address is required';
        if (!formData.senderCity) newErrors.senderCity = 'City is required';
        if (!formData.senderPostalCode) newErrors.senderPostalCode = 'Postal code is required';
        if (!formData.senderCountry) newErrors.senderCountry = 'Country is required';
        break;

      case 4: // Receiver Information
        if (!formData.receiverName) newErrors.receiverName = 'Receiver name is required';
        if (!formData.receiverPhone) newErrors.receiverPhone = 'Receiver phone is required';
        if (!formData.receiverEmail) newErrors.receiverEmail = 'Receiver email is required';
        if (!formData.receiverAddress) newErrors.receiverAddress = 'Receiver address is required';
        if (!formData.receiverCity) newErrors.receiverCity = 'City is required';
        if (!formData.receiverPostalCode) newErrors.receiverPostalCode = 'Postal code is required';
        if (!formData.receiverCountry) newErrors.receiverCountry = 'Country is required';
        break;

      case 5: // Shipping Options
        if (!formData.priority) newErrors.priority = 'Priority level is required';
        if (!formData.estimatedDelivery) newErrors.estimatedDelivery = 'Estimated delivery date is required';
        break;

      case 6: // Additional Services
        if (formData.additionalServices.temperatureControl && !formData.additionalServices.temperatureRange) {
          newErrors.temperatureRange = 'Temperature range is required for temperature control';
        }
        if (formData.additionalServices.specialHandling && !formData.additionalServices.specialHandlingType) {
          newErrors.specialHandlingType = 'Special handling type is required';
        }
        break;

      case 7: // Payment
        if (!formData.paymentMethod) newErrors.paymentMethod = 'Payment method is required';
        if (formData.paymentMethod === 'credit') {
          if (!formData.cardNumber) newErrors.cardNumber = 'Card number is required';
          if (!formData.expiryDate) newErrors.expiryDate = 'Expiry date is required';
          if (!formData.cvv) newErrors.cvv = 'CVV is required';
          if (!formData.billingAddress) newErrors.billingAddress = 'Billing address is required';
          if (!formData.billingPostalCode) newErrors.billingPostalCode = 'Billing postal code is required';
        }
        break;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, steps.length));
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo(0, 0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep(currentStep)) {
      setIsSubmitting(true);
      try {
        // Simulate API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        
        // Generate tracking number if not exists
        if (!formData.trackingNumber) {
          const prefix = 'SWF';
          const timestamp = Date.now().toString().slice(-6);
          const random = Math.random().toString(36).substring(2, 6).toUpperCase();
          formData.trackingNumber = `${prefix}${timestamp}${random}`;
        }

        console.log('New shipment data:', formData);
        alert('Shipment created successfully!');
        navigate('/track/' + formData.trackingNumber);
      } catch (error) {
        console.error('Error creating shipment:', error);
        alert('Error creating shipment. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const generateTrackingNumber = () => {
    const prefix = 'SWF';
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 6).toUpperCase();
    const trackingNum = `${prefix}${timestamp}${random}`;
    setFormData(prev => ({ ...prev, trackingNumber: trackingNum }));
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="mb-6 sm:mb-8">
              <label className="block text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-6">Service Type</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {serviceTypes.map((service) => (
                  <div
                    key={service.value}
                    className={`p-4 sm:p-6 border-2 rounded-xl cursor-pointer text-center transition-all duration-300 ${
                      formData.serviceType === service.value
                        ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
                        : 'border-white/20 hover:border-blue-400/50'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, serviceType: service.value }))}
                  >
                    <div className="text-blue-400 mb-2 sm:mb-3 flex justify-center">
                      {service.icon}
                    </div>
                    <div className="text-base sm:text-xl font-bold text-white">{service.label}</div>
                    <div className="text-sm sm:text-lg text-white/90 mt-1 sm:mt-2">{service.description}</div>
                    <div className="text-xs sm:text-base text-white/70 mt-2 sm:mt-3">From ${service.price}</div>
                  </div>
                ))}
              </div>
              {errors.serviceType && (
                <p className="text-red-400 text-sm sm:text-lg mt-2 sm:mt-3">{errors.serviceType}</p>
              )}
            </div>

            <div className="mb-6 sm:mb-8">
              <label className="block text-lg sm:text-xl font-semibold text-white mb-3 sm:mb-6">Package Type</label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                {packageTypes.map((pkg) => (
                  <div
                    key={pkg.value}
                    className={`p-4 sm:p-6 border-2 rounded-xl cursor-pointer text-center transition-all duration-300 ${
                      formData.packageType === pkg.value
                        ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
                        : 'border-white/20 hover:border-blue-400/50'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, packageType: pkg.value }))}
                  >
                    <div className="text-blue-400 mb-2 sm:mb-3 flex justify-center">
                      {pkg.icon}
                    </div>
                    <div className="text-base sm:text-xl font-bold text-white">{pkg.label}</div>
                    <div className="text-sm sm:text-lg text-white/90 mt-1 sm:mt-2">{pkg.description}</div>
                    <div className="text-xs sm:text-base text-white/70 mt-2 sm:mt-3">Max: {pkg.maxWeight} kg</div>
                  </div>
                ))}
              </div>
              {errors.packageType && (
                <p className="text-red-400 text-sm sm:text-lg mt-2 sm:mt-3">{errors.packageType}</p>
              )}
            </div>

            {/* Package Details Form */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-8">
              <div>
                <label className="block text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Weight (kg)</label>
                <div className="relative">
                  <input
                    type="number"
                    name="weight"
                    value={formData.weight}
                    onChange={handleChange}
                    className={`w-full p-3 sm:p-4 text-base sm:text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.weight ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="0.0"
                    step="0.1"
                  />
                  <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-white/60">
                    <ScaleIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                </div>
                {errors.weight && (
                  <p className="text-red-400 text-sm sm:text-lg mt-2">{errors.weight}</p>
                )}
              </div>

              <div>
                <label className="block text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Dimensions (L×W×H cm)</label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="number"
                    name="dimensions.length"
                    value={formData.dimensions.length}
                    onChange={handleChange}
                    className="w-full p-3 sm:p-4 bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Length"
                  />
                  <input
                    type="number"
                    name="dimensions.width"
                    value={formData.dimensions.width}
                    onChange={handleChange}
                    className="w-full p-3 sm:p-4 bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Width"
                  />
                  <input
                    type="number"
                    name="dimensions.height"
                    value={formData.dimensions.height}
                    onChange={handleChange}
                    className="w-full p-3 sm:p-4 bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Height"
                  />
                </div>
              </div>

              <div>
                <label className="block text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Declared Value ({CURRENCY.symbol})</label>
                <div className="relative">
                  <input
                    type="number"
                    name="value"
                    value={formData.value}
                    onChange={handleChange}
                    className="w-full p-3 sm:p-4 bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="0.00"
                    step="0.01"
                  />
                  <div className="absolute right-3 sm:right-4 top-1/2 transform -translate-y-1/2 text-white/60">
                    <CurrencyDollarIcon className="w-5 h-5 sm:w-6 sm:h-6" />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Quantity</label>
                <input
                  type="number"
                  name="quantity"
                  value={formData.quantity}
                  onChange={handleChange}
                  min="1"
                  className="w-full p-3 sm:p-4 bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Package Description */}
            <div className="mt-4 sm:mt-6">
              <label className="block text-base sm:text-lg font-semibold text-white mb-2 sm:mb-3">Package Description</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows="3"
                className={`w-full p-3 sm:p-4 text-base sm:text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.description ? 'border-red-500' : 'border-white/20'
                }`}
                placeholder="Describe the contents of your package..."
              />
              {errors.description && (
                <p className="text-red-400 text-sm sm:text-lg mt-2">{errors.description}</p>
              )}
            </div>

            {/* Additional Options */}
            <div className="mt-4 sm:mt-6 space-y-3 sm:space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="isFragile"
                  name="isFragile"
                  checked={formData.isFragile}
                  onChange={handleChange}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor="isFragile" className="text-base sm:text-lg text-white">Fragile Package</label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="requiresSignature"
                  name="requiresSignature"
                  checked={formData.requiresSignature}
                  onChange={handleChange}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor="requiresSignature" className="text-base sm:text-lg text-white">Requires Signature</label>
              </div>
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="insurance"
                  name="insurance"
                  checked={formData.insurance}
                  onChange={handleChange}
                  className="w-4 h-4 sm:w-5 sm:h-5 rounded border-white/20 bg-white/5 text-blue-500 focus:ring-blue-500"
                />
                <label htmlFor="insurance" className="text-base sm:text-lg text-white">Add Insurance</label>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-8 border border-white/20 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Pickup or Dropoff</h3>
              
              <div className="mb-8">
                <label className="block text-xl font-semibold text-white mb-6">Select Option</label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div
                    className={`p-8 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                      formData.pickupType === 'dropoff'
                        ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
                        : 'border-white/20 hover:border-blue-400/50'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, pickupType: 'dropoff' }))}
                  >
                    <div className="text-4xl mb-4">📦</div>
                    <h4 className="text-2xl font-bold text-white mb-3">Dropoff at Location</h4>
                    <p className="text-lg text-white/90">Bring your package to our store</p>
                  </div>
                  
                  <div
                    className={`p-8 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                      formData.pickupType === 'pickup'
                        ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20'
                        : 'border-white/20 hover:border-blue-400/50'
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, pickupType: 'pickup' }))}
                  >
                    <div className="text-4xl mb-4">🚚</div>
                    <h4 className="text-2xl font-bold text-white mb-3">Schedule Pickup</h4>
                    <p className="text-lg text-white/90">We'll pick up your package from your location</p>
                  </div>
                </div>
                {errors.pickupType && <p className="text-red-400 text-lg mt-3">{errors.pickupType}</p>}
              </div>

              {formData.pickupType === 'dropoff' ? (
                <div className="space-y-6">
                  <div>
                    <label className="block text-xl font-semibold text-white mb-3">Select Dropoff Location</label>
                    <select
                      name="preferredDropoffLocation"
                      value={formData.preferredDropoffLocation}
                      onChange={handleChange}
                      className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.preferredDropoffLocation ? 'border-red-500' : 'border-white/20'
                      }`}
                    >
                      <option value="">Select a location</option>
                      {dropoffLocations.map(location => (
                        <option key={location.value} value={location.value}>
                          {location.label} - {location.address}
                        </option>
                      ))}
                    </select>
                    {errors.preferredDropoffLocation && (
                      <p className="text-red-400 text-lg mt-2">{errors.preferredDropoffLocation}</p>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xl font-semibold text-white mb-3">Pickup Date</label>
                      <input
                        type="date"
                        name="pickupDate"
                        value={formData.pickupDate}
                        onChange={handleChange}
                        className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                          errors.pickupDate ? 'border-red-500' : 'border-white/20'
                        }`}
                      />
                      {errors.pickupDate && <p className="text-red-400 text-lg mt-2">{errors.pickupDate}</p>}
                    </div>
                    
                    <div>
                      <label className="block text-xl font-semibold text-white mb-3">Pickup Time</label>
                      <input
                        type="time"
                        name="pickupTime"
                        value={formData.pickupTime}
                        onChange={handleChange}
                        className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-xl font-semibold text-white mb-3">Pickup Instructions</label>
                    <textarea
                      name="pickupInstructions"
                      value={formData.pickupInstructions}
                      onChange={handleChange}
                      rows="3"
                      className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Any special instructions for pickup..."
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-8 border border-white/20 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Sender Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Full Name</label>
                  <input
                    type="text"
                    name="senderName"
                    value={formData.senderName}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.senderName ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="Enter sender's full name"
                  />
                  {errors.senderName && <p className="text-red-400 text-lg mt-2">{errors.senderName}</p>}
                </div>
                
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Phone Number</label>
                  <input
                    type="tel"
                    name="senderPhone"
                    value={formData.senderPhone}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.senderPhone ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="+27 74 258 6662"
                  />
                  {errors.senderPhone && <p className="text-red-400 text-lg mt-2">{errors.senderPhone}</p>}
                </div>
                
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Email Address</label>
                  <input
                    type="email"
                    name="senderEmail"
                    value={formData.senderEmail}
                    onChange={handleChange}
                    className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="sender@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Company Name</label>
                  <input
                    type="text"
                    name="senderCompany"
                    value={formData.senderCompany}
                    onChange={handleChange}
                    className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter company name (optional)"
                  />
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-xl font-semibold text-white mb-3">Complete Address</label>
                <textarea
                  name="senderAddress"
                  value={formData.senderAddress}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.senderAddress ? 'border-red-500' : 'border-white/20'
                  }`}
                  placeholder="Enter complete pickup address"
                />
                {errors.senderAddress && <p className="text-red-400 text-lg mt-2">{errors.senderAddress}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">City</label>
                  <input
                    type="text"
                    name="senderCity"
                    value={formData.senderCity}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.senderCity ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="Enter city"
                  />
                  {errors.senderCity && <p className="text-red-400 text-lg mt-2">{errors.senderCity}</p>}
                </div>

                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Country</label>
                  <select
                    name="senderCountry"
                    value={formData.senderCountry}
                    onChange={handleChange}
                    required
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.senderCountry ? 'border-red-500' : 'border-white/20'
                    }`}
                  >
                    <option value="">Select Country</option>
                    {COUNTRIES.map(country => (
                      <option key={country.value} value={country.value}>{country.label}</option>
                    ))}
                  </select>
                  {errors.senderCountry && <p className="text-red-400 text-lg mt-2">{errors.senderCountry}</p>}
                </div>

                <div>
                  <label className="block text-xl font-semibold text-white mb-3">
                    {formData.senderCountry ? getStateProvinceLabel(formData.senderCountry) : 'State/Province'}
                  </label>
                  {hasPredefinedStates(formData.senderCountry) ? (
                    <select
                      name="senderStateProvince"
                      value={formData.senderStateProvince}
                      onChange={handleChange}
                      className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.senderStateProvince ? 'border-red-500' : 'border-white/20'
                      }`}
                    >
                      <option value="">Select {getStateProvinceLabel(formData.senderCountry)}</option>
                      {getStatesForCountry(formData.senderCountry)?.map(state => (
                        <option key={state.value} value={state.value}>{state.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="senderStateProvince"
                      value={formData.senderStateProvince}
                      onChange={handleChange}
                      className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.senderStateProvince ? 'border-red-500' : 'border-white/20'
                      }`}
                      placeholder="Enter state or province"
                    />
                  )}
                  {errors.senderStateProvince && <p className="text-red-400 text-lg mt-2">{errors.senderStateProvince}</p>}
                </div>

                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Postal/ZIP Code</label>
                  <input
                    type="text"
                    name="senderPostalCode"
                    value={formData.senderPostalCode}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.senderPostalCode ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="Enter postal or ZIP code"
                  />
                  {errors.senderPostalCode && <p className="text-red-400 text-lg mt-2">{errors.senderPostalCode}</p>}
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-8 border border-white/20 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Receiver Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Full Name</label>
                  <input
                    type="text"
                    name="receiverName"
                    value={formData.receiverName}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.receiverName ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="Enter receiver's full name"
                  />
                  {errors.receiverName && <p className="text-red-400 text-lg mt-2">{errors.receiverName}</p>}
                </div>
                
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Phone Number</label>
                  <input
                    type="tel"
                    name="receiverPhone"
                    value={formData.receiverPhone}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.receiverPhone ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="+27 74 258 6662"
                  />
                  {errors.receiverPhone && <p className="text-red-400 text-lg mt-2">{errors.receiverPhone}</p>}
                </div>
                
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Email Address</label>
                  <input
                    type="email"
                    name="receiverEmail"
                    value={formData.receiverEmail}
                    onChange={handleChange}
                    className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="receiver@example.com"
                  />
                </div>
                
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Company Name</label>
                  <input
                    type="text"
                    name="receiverCompany"
                    value={formData.receiverCompany}
                    onChange={handleChange}
                    className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter company name (optional)"
                  />
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-xl font-semibold text-white mb-3">Delivery Address</label>
                <textarea
                  name="receiverAddress"
                  value={formData.receiverAddress}
                  onChange={handleChange}
                  rows="3"
                  className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.receiverAddress ? 'border-red-500' : 'border-white/20'
                  }`}
                  placeholder="Enter complete delivery address"
                />
                {errors.receiverAddress && <p className="text-red-400 text-lg mt-2">{errors.receiverAddress}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">City</label>
                  <input
                    type="text"
                    name="receiverCity"
                    value={formData.receiverCity}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.receiverCity ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="Enter city"
                  />
                  {errors.receiverCity && <p className="text-red-400 text-lg mt-2">{errors.receiverCity}</p>}
                </div>

                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Country</label>
                  <select
                    name="receiverCountry"
                    value={formData.receiverCountry}
                    onChange={handleChange}
                    required
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.receiverCountry ? 'border-red-500' : 'border-white/20'
                    }`}
                  >
                    <option value="">Select Country</option>
                    {COUNTRIES.map(country => (
                      <option key={country.value} value={country.value}>{country.label}</option>
                    ))}
                  </select>
                  {errors.receiverCountry && <p className="text-red-400 text-lg mt-2">{errors.receiverCountry}</p>}
                </div>

                <div>
                  <label className="block text-xl font-semibold text-white mb-3">
                    {formData.receiverCountry ? getStateProvinceLabel(formData.receiverCountry) : 'State/Province'}
                  </label>
                  {hasPredefinedStates(formData.receiverCountry) ? (
                    <select
                      name="receiverStateProvince"
                      value={formData.receiverStateProvince}
                      onChange={handleChange}
                      className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.receiverStateProvince ? 'border-red-500' : 'border-white/20'
                      }`}
                    >
                      <option value="">Select {getStateProvinceLabel(formData.receiverCountry)}</option>
                      {getStatesForCountry(formData.receiverCountry)?.map(state => (
                        <option key={state.value} value={state.value}>{state.label}</option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      name="receiverStateProvince"
                      value={formData.receiverStateProvince}
                      onChange={handleChange}
                      className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                        errors.receiverStateProvince ? 'border-red-500' : 'border-white/20'
                      }`}
                      placeholder="Enter state or province"
                    />
                  )}
                  {errors.receiverStateProvince && <p className="text-red-400 text-lg mt-2">{errors.receiverStateProvince}</p>}
                </div>

                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Postal/ZIP Code</label>
                  <input
                    type="text"
                    name="receiverPostalCode"
                    value={formData.receiverPostalCode}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.receiverPostalCode ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="Enter postal or ZIP code"
                  />
                  {errors.receiverPostalCode && <p className="text-red-400 text-lg mt-2">{errors.receiverPostalCode}</p>}
                </div>
              </div>
              
              <div className="mt-6">
                <label className="block text-xl font-semibold text-white mb-3">Special Instructions</label>
                <textarea
                  name="specialInstructions"
                  value={formData.specialInstructions}
                  onChange={handleChange}
                  rows="2"
                  className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Any special delivery instructions..."
                />
              </div>
            </div>
          </div>
        );

      case 5:
        return (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-8 border border-white/20 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Shipping Options</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Priority Level</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High Priority</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Estimated Delivery</label>
                  <input
                    type="datetime-local"
                    name="estimatedDelivery"
                    value={formData.estimatedDelivery}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.estimatedDelivery ? 'border-red-500' : 'border-white/20'
                    }`}
                  />
                  {errors.estimatedDelivery && <p className="text-red-400 text-lg mt-2">{errors.estimatedDelivery}</p>}
                </div>

                <div>
                  <label className="block text-xl font-semibold text-white mb-3">Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Pending">Pending Pickup</option>
                    <option value="In Transit">In Transit</option>
                    <option value="Out for Delivery">Out for Delivery</option>
                    <option value="Delivered">Delivered</option>
                    <option value="Exception">Exception</option>
                  </select>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                <h4 className="text-xl font-semibold text-white mb-4">Delivery Timeline</h4>
                {formData.timeline.map((entry, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <label className="block text-lg font-medium text-white/90 mb-2">Location</label>
                        <input
                          type="text"
                          value={entry.location}
                          onChange={(e) => handleTimelineChange(index, 'location', e.target.value)}
                          className="w-full p-3 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter location"
                        />
                      </div>
                      <div>
                        <label className="block text-lg font-medium text-white/90 mb-2">Status</label>
                        <input
                          type="text"
                          value={entry.status}
                          onChange={(e) => handleTimelineChange(index, 'status', e.target.value)}
                          className="w-full p-3 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter status"
                        />
                      </div>
                      <div>
                        <label className="block text-lg font-medium text-white/90 mb-2">Timestamp</label>
                        <input
                          type="datetime-local"
                          value={entry.timestamp}
                          onChange={(e) => handleTimelineChange(index, 'timestamp', e.target.value)}
                          className="w-full p-3 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                      </div>
                      <div>
                        <label className="block text-lg font-medium text-white/90 mb-2">Description</label>
                        <input
                          type="text"
                          value={entry.description}
                          onChange={(e) => handleTimelineChange(index, 'description', e.target.value)}
                          className="w-full p-3 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="Enter description"
                        />
                      </div>
                    </div>
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeTimelineEntry(index)}
                        className="mt-2 text-red-400 hover:text-red-300 text-lg"
                      >
                        Remove Entry
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addTimelineEntry}
                  className="mt-4 text-blue-400 hover:text-blue-300 text-lg"
                >
                  + Add Timeline Entry
                </button>
              </div>
            </div>
          </div>
        );

      case 6:
        return (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-8 border border-white/20 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Additional Services</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="flex items-center p-4 border border-white/20 rounded-xl">
                  <input
                    type="checkbox"
                    name="additionalServices.saturdayDelivery"
                    checked={formData.additionalServices.saturdayDelivery}
                    onChange={handleChange}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-white/20 rounded bg-white/5"
                  />
                  <label className="ml-3 block text-lg text-gray-700">Saturday Delivery</label>
                </div>
                <div className="flex items-center p-4 border border-white/20 rounded-xl">
                  <input
                    type="checkbox"
                    name="additionalServices.holdForPickup"
                    checked={formData.additionalServices.holdForPickup}
                    onChange={handleChange}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-white/20 rounded bg-white/5"
                  />
                  <label className="ml-3 block text-lg text-gray-700">Hold for Pickup</label>
                </div>
                <div className="flex items-center p-4 border border-white/20 rounded-xl">
                  <input
                    type="checkbox"
                    name="additionalServices.temperatureControl"
                    checked={formData.additionalServices.temperatureControl}
                    onChange={handleChange}
                    className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-white/20 rounded bg-white/5"
                  />
                  <label className="ml-3 block text-lg text-gray-700">Temperature Control</label>
                </div>
              </div>

              {formData.additionalServices.temperatureControl && (
                <div className="mb-6">
                  <label className="block text-xl font-semibold text-white mb-3">Temperature Range</label>
                  <input
                    type="text"
                    name="additionalServices.temperatureRange"
                    value={formData.additionalServices.temperatureRange}
                    onChange={handleChange}
                    className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                      errors.additionalServices.temperatureRange ? 'border-red-500' : 'border-white/20'
                    }`}
                    placeholder="e.g., 50°F to 70°F"
                  />
                </div>
              )}

              <div className="mb-6">
                <label className="block text-xl font-semibold text-white mb-3">Special Handling</label>
                <input
                  type="text"
                  name="additionalServices.specialHandlingType"
                  value={formData.additionalServices.specialHandlingType}
                  onChange={handleChange}
                  className={`w-full p-4 text-lg bg-white/5 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    errors.additionalServices.specialHandlingType ? 'border-red-500' : 'border-white/20'
                  }`}
                  placeholder="e.g., Fragile, Perishable"
                />
              </div>

              <div>
                <label className="block text-xl font-semibold text-white mb-3">Notification Preferences</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex items-center p-4 border border-white/20 rounded-xl">
                    <input
                      type="checkbox"
                      name="additionalServices.notificationPreferences.email"
                      checked={formData.additionalServices.notificationPreferences.email}
                      onChange={handleChange}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-white/20 rounded bg-white/5"
                    />
                    <label className="ml-3 block text-lg text-gray-700">Email</label>
                  </div>
                  <div className="flex items-center p-4 border border-white/20 rounded-xl">
                    <input
                      type="checkbox"
                      name="additionalServices.notificationPreferences.sms"
                      checked={formData.additionalServices.notificationPreferences.sms}
                      onChange={handleChange}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-white/20 rounded bg-white/5"
                    />
                    <label className="ml-3 block text-lg text-gray-700">SMS</label>
                  </div>
                  <div className="flex items-center p-4 border border-white/20 rounded-xl">
                    <input
                      type="checkbox"
                      name="additionalServices.notificationPreferences.push"
                      checked={formData.additionalServices.notificationPreferences.push}
                      onChange={handleChange}
                      className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-white/20 rounded bg-white/5"
                    />
                    <label className="ml-3 block text-lg text-gray-700">Push Notification</label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 7:
        return (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-8 border border-white/20 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Payment</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                    <h4 className="text-xl font-semibold text-white mb-4">Package Details</h4>
                    <div className="space-y-3">
                      <p className="text-lg text-white/90"><span className="font-medium">Tracking:</span> {formData.trackingNumber}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Service:</span> {serviceTypes.find(s => s.value === formData.serviceType)?.label}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Type:</span> {packageTypes.find(p => p.value === formData.packageType)?.label}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Quantity:</span> {formData.quantity}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Weight:</span> {formData.weight} kg</p>
                      {formData.dimensions.length && (
                        <p className="text-lg text-white/90">
                          <span className="font-medium">Dimensions:</span> {formData.dimensions.length}×{formData.dimensions.width}×{formData.dimensions.height}
                        </p>
                      )}
                      {formData.value && <p className="text-lg text-white/90"><span className="font-medium">Value:</span> {CURRENCY.symbol}{formData.value}</p>}
                      {formData.insurance && (
                        <p className="text-lg text-white/90"><span className="font-medium">Insurance:</span> {CURRENCY.symbol}{formData.insuranceValue}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                    <h4 className="text-xl font-semibold text-white mb-4">Pickup/Dropoff</h4>
                    <div className="space-y-3">
                      <p className="text-lg text-white/90"><span className="font-medium">Type:</span> {formData.pickupType === 'pickup' ? 'Pickup' : 'Dropoff'}</p>
                      {formData.pickupType === 'pickup' ? (
                        <>
                          <p className="text-lg text-white/90"><span className="font-medium">Date:</span> {formData.pickupDate}</p>
                          <p className="text-lg text-white/90"><span className="font-medium">Time:</span> {formData.pickupTime}</p>
                          {formData.pickupInstructions && (
                            <p className="text-lg text-white/90"><span className="font-medium">Instructions:</span> {formData.pickupInstructions}</p>
                          )}
                        </>
                      ) : (
                        <p className="text-lg text-white/90">
                          <span className="font-medium">Location:</span>{' '}
                          {dropoffLocations.find(l => l.value === formData.preferredDropoffLocation)?.label}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                    <h4 className="text-xl font-semibold text-white mb-4">Payment Method</h4>
                    <div className="space-y-4">
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="credit"
                          checked={formData.paymentMethod === 'credit'}
                          onChange={handleChange}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-white/20"
                        />
                        <label className="ml-3 block text-lg text-gray-700">Credit Card</label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value="account"
                          checked={formData.paymentMethod === 'account'}
                          onChange={handleChange}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-white/20"
                        />
                        <label className="ml-3 block text-lg text-gray-700">Account Payment</label>
                      </div>
                    </div>

                    {formData.paymentMethod === 'credit' && (
                      <div className="mt-6 space-y-4">
                        <div>
                          <label className="block text-lg font-medium text-white/90 mb-2">Card Number</label>
                          <input
                            type="text"
                            name="cardNumber"
                            value={formData.cardNumber}
                            onChange={handleChange}
                            className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="1234 5678 9012 3456"
                          />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-lg font-medium text-white/90 mb-2">Expiry Date</label>
                            <input
                              type="text"
                              name="expiryDate"
                              value={formData.expiryDate}
                              onChange={handleChange}
                              className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="MM/YY"
                            />
                          </div>
                          <div>
                            <label className="block text-lg font-medium text-white/90 mb-2">CVV</label>
                            <input
                              type="text"
                              name="cvv"
                              value={formData.cvv}
                              onChange={handleChange}
                              className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              placeholder="123"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {formData.paymentMethod === 'account' && (
                      <div className="mt-6 space-y-4">
                        <div>
                          <label className="block text-lg font-medium text-white/90 mb-2">Account Number</label>
                          <input
                            type="text"
                            name="accountNumber"
                            value={formData.accountNumber}
                            onChange={handleChange}
                            className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter account number"
                          />
                        </div>
                        <div>
                          <label className="block text-lg font-medium text-white/90 mb-2">Billing Reference</label>
                          <input
                            type="text"
                            name="billingReference"
                            value={formData.billingReference}
                            onChange={handleChange}
                            className="w-full p-4 text-lg bg-white/5 border border-white/20 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="Enter billing reference"
                          />
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                    <h4 className="text-xl font-semibold text-white mb-4">Total Amount</h4>
                    <div className="space-y-3">
                      <p className="text-lg text-white/90"><span className="font-medium">Base Price:</span> {CURRENCY.symbol}{serviceTypes.find(s => s.value === formData.serviceType)?.price}</p>
                      {formData.insurance && (
                        <p className="text-lg text-white/90"><span className="font-medium">Insurance:</span> {CURRENCY.symbol}{(parseFloat(formData.insuranceValue) * 0.02).toFixed(2)}</p>
                      )}
                      {formData.additionalServices.saturdayDelivery && (
                        <p className="text-lg text-white/90"><span className="font-medium">Saturday Delivery:</span> $25.99</p>
                      )}
                      {formData.additionalServices.holdForPickup && (
                        <p className="text-lg text-white/90"><span className="font-medium">Hold for Pickup:</span> $15.99</p>
                      )}
                      {formData.additionalServices.temperatureControl && (
                        <p className="text-lg text-white/90"><span className="font-medium">Temperature Control:</span> $35.99</p>
                      )}
                      <div className="border-t border-white/20 pt-3 mt-3">
                        <p className="text-xl font-bold text-white"><span className="font-medium">Total:</span> {CURRENCY.symbol}{calculatedPrice}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 8:
        return (
          <div className="space-y-6">
            <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-8 border border-white/20 shadow-xl">
              <h3 className="text-2xl font-bold text-white mb-6">Review & Submit</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="space-y-6">
                  <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                    <h4 className="text-xl font-semibold text-white mb-4">Package Details</h4>
                    <div className="space-y-3">
                      <p className="text-lg text-white/90"><span className="font-medium">Tracking:</span> {formData.trackingNumber}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Service:</span> {serviceTypes.find(s => s.value === formData.serviceType)?.label}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Type:</span> {packageTypes.find(p => p.value === formData.packageType)?.label}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Quantity:</span> {formData.quantity}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Weight:</span> {formData.weight} kg</p>
                      {formData.dimensions.length && (
                        <p className="text-lg text-white/90">
                          <span className="font-medium">Dimensions:</span> {formData.dimensions.length}×{formData.dimensions.width}×{formData.dimensions.height}
                        </p>
                      )}
                      {formData.value && <p className="text-lg text-white/90"><span className="font-medium">Value:</span> {CURRENCY.symbol}{formData.value}</p>}
                      {formData.insurance && (
                        <p className="text-lg text-white/90"><span className="font-medium">Insurance:</span> {CURRENCY.symbol}{formData.insuranceValue}</p>
                      )}
                    </div>
                  </div>
                  
                  <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                    <h4 className="text-xl font-semibold text-white mb-4">Sender Information</h4>
                    <div className="space-y-3">
                      <p className="text-lg text-white/90">{formData.senderName}</p>
                      <p className="text-lg text-white/90">{formData.senderPhone}</p>
                      <p className="text-lg text-white/90">{formData.senderEmail}</p>
                      <p className="text-lg text-white/90">{formData.senderAddress}</p>
                      <p className="text-lg text-white/90">
                        {formData.senderCity}, {formData.senderStateProvince} {formData.senderPostalCode}, {COUNTRIES.find(c => c.value === formData.senderCountry)?.label || formData.senderCountry}
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-6">
                  <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                    <h4 className="text-xl font-semibold text-white mb-4">Receiver Information</h4>
                    <div className="space-y-3">
                      <p className="text-lg text-white/90">{formData.receiverName}</p>
                      <p className="text-lg text-white/90">{formData.receiverPhone}</p>
                      <p className="text-lg text-white/90">{formData.receiverEmail}</p>
                      <p className="text-lg text-white/90">{formData.receiverAddress}</p>
                      <p className="text-lg text-white/90">
                        {formData.receiverCity}, {formData.receiverStateProvince} {formData.receiverPostalCode}, {COUNTRIES.find(c => c.value === formData.receiverCountry)?.label || formData.receiverCountry}
                      </p>
                      {formData.specialInstructions && (
                        <div className="mt-3">
                          <p className="text-lg font-medium text-white/90">Special Instructions:</p>
                          <p className="text-lg text-white/90">{formData.specialInstructions}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                    <h4 className="text-xl font-semibold text-white mb-4">Shipping Details</h4>
                    <div className="space-y-3">
                      <p className="text-lg text-white/90"><span className="font-medium">Status:</span> {formData.status}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Priority:</span> {formData.priority}</p>
                      <p className="text-lg text-white/90"><span className="font-medium">Estimated Delivery:</span> {formData.estimatedDelivery}</p>
                    </div>
                  </div>

                  <div className="bg-white/5 rounded-xl p-6 border border-white/20">
                    <h4 className="text-xl font-semibold text-white mb-4">Payment Summary</h4>
                    <div className="space-y-3">
                      <p className="text-lg text-white/90"><span className="font-medium">Method:</span> {formData.paymentMethod === 'credit' ? 'Credit Card' : 'Account Payment'}</p>
                      {formData.paymentMethod === 'credit' && (
                        <p className="text-lg text-white/90"><span className="font-medium">Card:</span> **** **** **** {formData.cardNumber.slice(-4)}</p>
                      )}
                      <div className="border-t border-white/20 pt-3 mt-3">
                        <p className="text-xl font-bold text-white"><span className="font-medium">Total Amount:</span> {CURRENCY.symbol}{calculatedPrice}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 pt-24 sm:pt-32 pb-12 px-2 sm:px-4 overflow-x-hidden">
      <div className="max-w-7xl mx-auto w-full">
        {/* Progress Steps */}
        <div className="mb-8 sm:mb-12">
          <div className="flex items-center justify-between overflow-x-auto pb-4">
            {steps.map((step, index) => (
              <div key={step.number} className="flex items-center flex-shrink-0">
                <div
                  className={`flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 ${
                    currentStep >= step.number
                      ? 'border-blue-500 bg-blue-500 text-white'
                      : 'border-white/20 text-white/40'
                  }`}
                >
                  {step.icon}
                </div>
                {index < steps.length - 1 && (
                  <div
                    className={`w-4 sm:w-16 h-0.5 ${
                      currentStep > step.number ? 'bg-blue-500' : 'bg-white/20'
                    }`}
                  />
                )}
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-2 sm:mt-4 overflow-x-auto pb-2">
            {steps.map((step) => (
              <div
                key={step.number}
                className={`text-xs sm:text-base font-medium flex-shrink-0 px-2 sm:px-4 text-center min-w-[80px] sm:min-w-0 ${
                  currentStep >= step.number ? 'text-white' : 'text-white/40'
                }`}
              >
                <div className="hidden sm:block">{step.title}</div>
                <div className="sm:hidden">
                  {step.title.split(' ').map((word, i) => (
                    <div key={i} className="leading-tight">{word}</div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white/10 backdrop-blur-2xl rounded-2xl p-3 sm:p-8 border border-white/20 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-8">
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8 sm:mt-12">
              <button
                onClick={prevStep}
                disabled={currentStep === 1}
                className={`flex items-center gap-2 px-3 sm:px-6 py-2 sm:py-3 rounded-xl text-sm sm:text-base font-medium transition-all ${
                  currentStep === 1
                    ? 'bg-white/10 text-white/40 cursor-not-allowed'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                <ArrowLeftIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                Previous
              </button>
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-3 sm:px-6 py-2 sm:py-3 bg-blue-600 text-white rounded-xl text-sm sm:text-base font-medium hover:bg-blue-700 transition-all"
              >
                {currentStep === steps.length ? 'Submit' : 'Next'}
                <ArrowRightIcon className="w-4 h-4 sm:w-5 sm:h-5" />
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default NewShipment;